import type { DeductionSource } from './types'

/**
 * When the quote screen sends the confidence level it computed. The level is saved against the quote
 * record, so a quote the agent never lets settle would stay without one. `settled` is the normal send,
 * once the quote request, the fare rules and the ticket selection are all in. `fallback` is the
 * provisional send (a timer, or the agent processing the cancellation first); a later `settled` send
 * replaces it, because the figure can still improve once the fare rules arrive.
 */
export type ConfidenceReportReason = 'settled' | 'fallback'

/** What has already been sent for the quote record on screen, by its id. */
export interface ConfidenceReportLog {
  /** Any send, settled or provisional. */
  reported: string | null
  /** The settled send. */
  settled: string | null
  /** An ops override of the level: nothing is sent automatically after it. */
  locked: string | null
}

export function emptyConfidenceReportLog(): ConfidenceReportLog {
  return { reported: null, settled: null, locked: null }
}

export function shouldReportConfidence(
  log: ConfidenceReportLog,
  requestId: string,
  reason: ConfidenceReportReason,
): boolean {
  if (log.locked === requestId) return false
  return reason === 'settled' ? log.settled !== requestId : log.reported !== requestId
}

export function recordConfidenceReport(
  log: ConfidenceReportLog,
  requestId: string,
  reason: ConfidenceReportReason,
): ConfidenceReportLog {
  return { ...log, reported: requestId, settled: reason === 'settled' ? requestId : log.settled }
}

export function lockConfidenceReports(log: ConfidenceReportLog, requestId: string): ConfidenceReportLog {
  return { ...log, reported: requestId, settled: requestId, locked: requestId }
}

export interface QuoteSettledInput {
  quoteLoading: boolean
  fareRulesLoading: boolean
  /** The source the current statement resolved from. A void window ignores the fare rules. */
  source: DeductionSource | undefined
  ticketCount: number
  selectedTicketCount: number
}

/**
 * Whether the statement can still change because the fare rules have not arrived: they can cap a
 * quoted penalty to the filed fee. A void window ignores them.
 */
export function isAwaitingFareRules(input: Pick<QuoteSettledInput, 'fareRulesLoading' | 'source'>): boolean {
  return input.fareRulesLoading && input.source !== 'VOID_WINDOW'
}

/** Whether the level on screen is the final one for this quote, so it is safe to save as the settled value. */
export function isQuoteSettled(input: QuoteSettledInput): boolean {
  if (input.quoteLoading) return false
  if (isAwaitingFareRules(input)) return false
  return input.ticketCount === 0 || input.selectedTicketCount > 0
}
